// ═══════════════════════════════════════════════════════════════════════════
// PAGE: REMPLACEMENT D'UTILISATEUR (A → B)
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Workflow : choisir A et B → preview backend → l'user édite ligne par ligne →
// apply → page de récap avec export CSV.

interface UrepUser {
  id: number;
  name?: string;
  trigram?: string | null;
  is_team?: boolean;
}

interface PreviewItem {
  kind: string;
  ref_id: number;
  extra_id?: number | null;
  ref_label?: string | null;
  extra_label?: string | null;
  role_label?: string | null;
  rule_individual_only?: boolean;
  default_replacement_user_id?: number | null;
  conflict_reason?: string | null;
}

interface PreviewData {
  from_user: UrepUser;
  to_user: UrepUser;
  items: PreviewItem[];
}

interface UrepRow {
  kind: string;
  ref_id: number;
  extra_id: number | null;
  ref_label: string | null;
  extra_label: string | null;
  role_label: string | null;
  rule_individual_only: boolean;
  replacement_user: UrepUser | null;
  skipped: boolean;
  defaultConflictReason: string | null;
}

interface ApplyResultItem {
  status: 'applied' | 'no_op' | 'skipped' | 'error' | string;
  ref_id: number;
  ref_label?: string | null;
  extra_id?: number | null;
  extra_label?: string | null;
  role_label?: string | null;
  kind: string;
  replacement_user_label?: string | null;
  note?: string | null;
}

interface ApplyResult {
  results: ApplyResultItem[];
  counts: { applied: number; no_op: number; skipped: number; errors: number };
}

(function () {
  const state: {
    fromUser: UrepUser | null;
    toUser: UrepUser | null;
    preview: PreviewData | null;
    rows: UrepRow[];
    applyResult: ApplyResult | null;
  } = {
    fromUser: null,
    toUser: null,
    preview: null,
    rows: [],
    applyResult: null,
  };

  function reset(): void {
    state.fromUser = null;
    state.toUser = null;
    state.preview = null;
    state.rows = [];
    state.applyResult = null;
  }

  function userChip(u: UrepUser | null): string {
    if (!u) return '<span style="color:var(--text3)">—</span>';
    const trig = (u.trigram || '').toString().toUpperCase();
    const team = u.is_team ? ' 👥' : '';
    return `<span style="display:inline-flex;align-items:center;gap:4px">
      <span style="background:var(--bg3);padding:1px 6px;border-radius:4px;font-size:11px;font-weight:600">${esc(trig || '?')}</span>
      <span>${esc(u.name || '')}${team}</span>
    </span>`;
  }

  function userRowAligned(u: UrepUser | null): string {
    if (!u)
      return `<span style="color:var(--text3)">${esc(t('userReplacement.empty.user'))}</span>`;
    const trig = (u.trigram || '').toString().toUpperCase();
    const team = u.is_team ? ' 👥' : '';
    return `<span style="display:inline-grid;grid-template-columns:64px 1fr;align-items:center;gap:8px">
      <span style="background:var(--bg3);padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;text-align:center">${esc(trig || '?')}</span>
      <span>${esc(u.name || '')}${team}</span>
    </span>`;
  }

  function roleLabelFull(row: UrepRow): string {
    if (
      (row.kind === 'project_committee_pilot' || row.kind === 'program_committee_pilot') &&
      row.extra_label
    ) {
      return `${row.role_label || ''} — ${row.extra_label}`;
    }
    return row.role_label || '';
  }

  function pillHtml(status: 'ok' | 'rule_violation' | 'skipped'): string {
    if (status === 'skipped') {
      return `<span title="${esc(t('userReplacement.pill.skipped'))}" style="display:inline-block;width:12px;height:12px;border-radius:50%;background:var(--text3)"></span>`;
    }
    if (status === 'rule_violation') {
      return `<span title="${esc(t('userReplacement.pill.violation'))}" style="display:inline-block;width:12px;height:12px;border-radius:50%;background:var(--danger,#d33)"></span>`;
    }
    return `<span title="${esc(t('userReplacement.pill.ok'))}" style="display:inline-block;width:12px;height:12px;border-radius:50%;background:var(--ok,#1aa260)"></span>`;
  }

  function rowStatus(row: UrepRow): 'ok' | 'rule_violation' | 'skipped' {
    if (row.skipped) return 'skipped';
    if (!row.replacement_user) return 'rule_violation';
    if (row.rule_individual_only && row.replacement_user.is_team) return 'rule_violation';
    return 'ok';
  }

  function pickFrom(): void {
    // Cas particulier (cf. user 2026-05-21) : ce picker DOIT inclure les
    // utilisateurs inactifs / partis — c'est précisément eux que l'on cherche
    // à remplacer dans la majorité des cas d'usage.
    window.showUserPicker(
      (u: UrepUser) => {
        state.fromUser = u;
        render();
      },
      { title: t('userReplacement.picker.fromTitle'), includeInactive: true },
    );
  }

  function pickTo(): void {
    window.showUserPicker(
      (u: UrepUser) => {
        state.toUser = u;
        render();
      },
      { title: t('userReplacement.picker.toTitle') },
    );
  }

  function pickRowReplacement(idx: number): void {
    const row = state.rows[idx];
    if (!row) return;
    window.showUserPicker(
      (u: UrepUser) => {
        row.replacement_user = u;
        row.skipped = false;
        render();
      },
      { title: t('userReplacement.picker.rowTitle') },
    );
  }

  function toggleSkip(idx: number): void {
    const row = state.rows[idx];
    if (!row) return;
    if (row.skipped) {
      row.skipped = false;
      if (!row.replacement_user) {
        row.replacement_user = state.toUser;
      }
    } else {
      row.skipped = true;
    }
    render();
  }

  async function runPreview(): Promise<void> {
    if (!state.fromUser || !state.toUser) return;
    try {
      const data = (await api.previewUserReplacement({
        from_user_id: state.fromUser.id,
        to_user_id: state.toUser.id,
      })) as PreviewData;
      state.preview = data;
      const usersById = await loadUsersById();
      state.rows = (data.items || []).map((it) => ({
        kind: it.kind,
        ref_id: it.ref_id,
        extra_id: it.extra_id ?? null,
        ref_label: it.ref_label ?? null,
        extra_label: it.extra_label ?? null,
        role_label: it.role_label ?? null,
        rule_individual_only: !!it.rule_individual_only,
        replacement_user: it.default_replacement_user_id
          ? usersById[it.default_replacement_user_id] || state.toUser
          : null,
        skipped: false,
        defaultConflictReason: it.conflict_reason ?? null,
      }));
      render();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('userReplacement.toast.previewError');
      toast(msg, 'error');
    }
  }

  async function loadUsersById(): Promise<Record<number, UrepUser>> {
    const users = (await loadUsers()) as UrepUser[];
    const out: Record<number, UrepUser> = {};
    for (const u of users) out[u.id] = u;
    return out;
  }

  function buildApplyPayload(): {
    from_user_id: number;
    to_user_id: number;
    items: Array<{
      kind: string;
      ref_id: number;
      extra_id: number | null;
      replacement_user_id: number | null;
      skip: boolean;
    }>;
  } {
    const items = state.rows.map((row) => {
      const rs = rowStatus(row);
      const skip = row.skipped || rs === 'rule_violation';
      return {
        kind: row.kind,
        ref_id: row.ref_id,
        extra_id: row.extra_id,
        replacement_user_id: skip ? null : (row.replacement_user?.id ?? null),
        skip,
      };
    });
    return {
      from_user_id: state.fromUser?.id ?? 0,
      to_user_id: state.toUser?.id ?? 0,
      items,
    };
  }

  function confirmApply(): void {
    const payload = buildApplyPayload();
    const toApply = payload.items.filter((it) => !it.skip).length;
    const toSkip = payload.items.length - toApply;
    const msg = `
      <p style="margin:0 0 8px 0">${t('userReplacement.confirm.intro', { n: toApply })}
      ${toSkip > 0 ? `<br>${t('userReplacement.confirm.skipped', { n: toSkip })}` : ''}</p>
      <p style="margin:0;padding:10px;background:var(--bg2);border-left:3px solid var(--danger,#d33);font-weight:500">
        ${esc(t('userReplacement.confirm.warning'))}
      </p>`;
    openConfirmDangerModal(
      t('userReplacement.btn.confirm'),
      msg,
      async () => {
        try {
          const res = (await api.applyUserReplacement(payload)) as ApplyResult;
          (res.results || []).forEach((r, i) => {
            const src = state.rows[i];
            if (!src) return;
            if (!r.ref_label) r.ref_label = src.ref_label || '';
            if (!r.extra_label) r.extra_label = src.extra_label || null;
            if (!r.replacement_user_label && src.replacement_user) {
              const trig = (src.replacement_user.trigram || '').toString().toUpperCase();
              const name = src.replacement_user.name || '';
              r.replacement_user_label = trig ? `${trig} — ${name}` : name;
            }
          });
          state.applyResult = res;
          render();
          toast(
            t('userReplacement.toast.done', {
              applied: res.counts.applied,
              errors: res.counts.errors,
            }),
            res.counts.errors ? 'error' : 'ok',
          );
        } catch (e: unknown) {
          const m = e instanceof Error ? e.message : t('userReplacement.toast.applyError');
          toast(m, 'error');
          throw e;
        }
      },
      { confirmLabel: t('userReplacement.btn.confirm') },
    );
  }

  function exportRecapCsv(): void {
    if (!state.applyResult) return;
    const rows: string[][] = [
      [
        t('userReplacement.csv.statut'),
        t('userReplacement.csv.domaine'),
        t('userReplacement.csv.reference'),
        t('userReplacement.csv.detail'),
        t('userReplacement.csv.replacement'),
        t('userReplacement.csv.note'),
      ],
    ];
    for (const r of state.applyResult.results) {
      rows.push([
        r.status,
        r.role_label || '',
        r.ref_label || '',
        r.extra_id != null ? `id=${r.extra_id}` : '',
        r.replacement_user_label || '',
        r.note || '',
      ]);
    }
    const csv = rows
      .map((line) =>
        line
          .map((cell) => {
            const s = String(cell ?? '');
            return `"${s.replace(/"/g, '""')}"`;
          })
          .join(';'),
      )
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const fromTrig = (state.preview?.from_user?.trigram || state.fromUser?.trigram || '')
      .toString()
      .toUpperCase();
    const toTrig = (state.preview?.to_user?.trigram || state.toUser?.trigram || '')
      .toString()
      .toUpperCase();
    const today = new Date().toISOString().slice(0, 10);
    api.triggerBlobDownload(
      blob,
      `remplacement_${fromTrig || 'A'}_vers_${toTrig || 'B'}_${today}.csv`,
    );
  }

  function renderSelectors(): string {
    const labelStyle =
      'font-size:11px;font-weight:600;color:var(--text3);text-transform:uppercase;letter-spacing:.05em';
    return `
      <div class="card" style="margin-bottom:16px">
        <div style="display:grid;grid-template-columns:max-content 1fr max-content;align-items:center;gap:10px 16px">
          <div style="${labelStyle}">${esc(t('userReplacement.label.from'))}</div>
          <div>${userRowAligned(state.fromUser)}</div>
          <button class="btn btn-secondary btn-sm" id="urep-pick-from">${esc(t(state.fromUser ? 'userReplacement.btn.change' : 'userReplacement.btn.choose'))}</button>

          <div style="${labelStyle}">${esc(t('userReplacement.label.to'))}</div>
          <div>${userRowAligned(state.toUser)}</div>
          <button class="btn btn-secondary btn-sm" id="urep-pick-to">${esc(t(state.toUser ? 'userReplacement.btn.change' : 'userReplacement.btn.choose'))}</button>
        </div>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:16px">
        <button class="btn btn-primary" id="urep-preview-btn" ${!state.fromUser || !state.toUser ? 'disabled' : ''}>
          ${esc(t('userReplacement.btn.preview'))}
        </button>
      </div>
    `;
  }

  function renderPreviewTable(): string {
    if (!state.preview) {
      return `<div class="card" style="color:var(--text3)">
        ${esc(t('userReplacement.empty.preview'))}
      </div>`;
    }
    if (!state.rows.length) {
      return `<div class="card" style="text-align:center;padding:24px">
        <div style="font-size:14px;font-weight:500">${esc(t('userReplacement.empty.results'))}</div>
        <div style="font-size:12px;color:var(--text3);margin-top:6px">
          ${t('userReplacement.empty.resultsDetail', { name: state.fromUser?.name ?? '' })}
        </div>
      </div>`;
    }
    const head = `<thead><tr>
      <th style="width:24px"></th>
      <th style="width:80px">${esc(t('userReplacement.col.id'))}</th>
      <th>${esc(t('userReplacement.col.ref'))}</th>
      <th>${esc(t('userReplacement.col.role'))}</th>
      <th>${esc(t('userReplacement.col.replacement'))}</th>
      <th style="white-space:nowrap">${esc(t('userReplacement.col.action'))}</th>
    </tr></thead>`;
    const body = state.rows
      .map((row, idx) => {
        const status = rowStatus(row);
        const dim = row.skipped ? 'opacity:.5;text-decoration:line-through' : '';
        const replacementHtml = row.skipped
          ? '<span style="color:var(--text3)">—</span>'
          : row.replacement_user
            ? userChip(row.replacement_user)
            : `<span style="color:var(--danger,#d33)">${t('userReplacement.row.toChoose', { reason: row.defaultConflictReason || t('userReplacement.row.violation') })}</span>`;
        const actionsInner = row.skipped
          ? `<button class="btn btn-secondary btn-sm" style="white-space:nowrap" data-urep-restore="${idx}">${esc(t('userReplacement.btn.restore'))}</button>`
          : `<button class="btn btn-secondary btn-sm" style="white-space:nowrap" data-urep-pick="${idx}">${esc(t('userReplacement.btn.pickReplacement'))}</button>
           <button class="btn btn-neutral btn-sm" style="white-space:nowrap" data-urep-skip="${idx}">${esc(t('userReplacement.btn.skip'))}</button>`;
        const actions = `<div style="display:flex;flex-wrap:nowrap;gap:6px;justify-content:flex-end">${actionsInner}</div>`;
        return `<tr style="${dim}">
        <td>${pillHtml(status)}</td>
        <td style="font-size:12px;color:var(--text3)">#${row.ref_id}</td>
        <td>${esc(row.ref_label || '')}</td>
        <td style="font-size:12px;color:var(--text3)">${esc(roleLabelFull(row))}</td>
        <td>${replacementHtml}</td>
        <td>${actions}</td>
      </tr>`;
      })
      .join('');
    const blockingCount = state.rows.filter(
      (r) => rowStatus(r) === 'rule_violation' && !r.skipped,
    ).length;
    const validateBtn = (
      id: string,
    ): string => `<button class="btn btn-primary" id="${id}" ${blockingCount ? 'disabled' : ''} title="${blockingCount ? esc(t('userReplacement.row.blockingTitle', { n: blockingCount })) : ''}">
        ${esc(t('userReplacement.btn.validateAll'))}
      </button>`;
    return `<div class="card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
        <div style="font-size:11px;color:var(--text3);text-transform:uppercase;letter-spacing:.05em">
          ${t('userReplacement.row.refCount', { n: state.rows.length })}
        </div>
        <div>${validateBtn('urep-apply-btn-top')}</div>
      </div>
      <table class="table" style="width:100%;border-collapse:collapse;font-size:13px">
        ${head}
        <tbody>${body}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-top:12px">
        ${validateBtn('urep-apply-btn-bottom')}
      </div>
    </div>`;
  }

  function renderRecap(): string {
    const r = state.applyResult;
    if (!r) return '';
    const head = `<thead><tr>
      <th style="width:24px"></th>
      <th style="width:80px">${esc(t('userReplacement.col.id'))}</th>
      <th>${esc(t('userReplacement.col.ref'))}</th>
      <th>${esc(t('userReplacement.col.role'))}</th>
      <th>${esc(t('userReplacement.col.replacementApplied'))}</th>
      <th>${esc(t('userReplacement.col.note'))}</th>
    </tr></thead>`;
    const body = r.results
      .map((item) => {
        let icon = '';
        let dim = '';
        if (item.status === 'applied')
          icon = '<span style="color:var(--ok,#1aa260);font-weight:bold">✓</span>';
        else if (item.status === 'no_op') icon = '<span style="color:var(--ok,#1aa260)">✓</span>';
        else if (item.status === 'skipped') {
          icon = '<span style="color:var(--text3)">—</span>';
          dim = 'opacity:.5;text-decoration:line-through';
        } else if (item.status === 'error')
          icon = '<span style="color:var(--danger,#d33);font-weight:bold">✗</span>';
        const role =
          (item.kind === 'project_committee_pilot' || item.kind === 'program_committee_pilot') &&
          item.extra_label
            ? `${item.role_label || ''} — ${item.extra_label}`
            : item.role_label || '';
        return `<tr style="${dim}">
        <td>${icon}</td>
        <td style="font-size:12px;color:var(--text3)">#${item.ref_id}</td>
        <td>${esc(item.ref_label || '')}</td>
        <td style="font-size:12px;color:var(--text3)">${esc(role)}</td>
        <td>${esc(item.replacement_user_label || '—')}</td>
        <td style="font-size:12px;color:var(--text3)">${esc(item.note || '')}</td>
      </tr>`;
      })
      .join('');
    return `<div class="card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;flex-wrap:wrap;gap:8px">
        <div>
          <div style="font-size:14px;font-weight:600">${esc(t('userReplacement.recap.title'))}</div>
          <div style="font-size:12px;color:var(--text3);margin-top:4px">
            ${esc(t('userReplacement.recap.applied'))} : <strong>${r.counts.applied}</strong> &nbsp;•&nbsp;
            ${esc(t('userReplacement.recap.noOp'))} : <strong>${r.counts.no_op}</strong> &nbsp;•&nbsp;
            ${esc(t('userReplacement.recap.skipped'))} : <strong>${r.counts.skipped}</strong> &nbsp;•&nbsp;
            ${esc(t('userReplacement.recap.errors'))} : <strong style="${r.counts.errors ? 'color:var(--danger,#d33)' : ''}">${r.counts.errors}</strong>
          </div>
        </div>
        <div style="display:flex;gap:8px">
          <button class="btn btn-secondary" id="urep-export-csv">${esc(t('userReplacement.btn.exportCsv'))}</button>
          <button class="btn btn-neutral" id="urep-restart">${esc(t('userReplacement.btn.restart'))}</button>
        </div>
      </div>
      <table class="table" style="width:100%;border-collapse:collapse;font-size:13px">
        ${head}
        <tbody>${body}</tbody>
      </table>
    </div>`;
  }

  function render(): void {
    if (state.applyResult) {
      setHeader(t('userReplacement.titleRecap'), '');
      setContent(renderRecap());
      wireRecap();
      return;
    }
    setHeader(t('userReplacement.title'), '');
    setContent(`
      <div style="max-width:1100px">
        ${renderSelectors()}
        ${renderPreviewTable()}
      </div>
    `);
    wireSelectors();
    wirePreviewTable();
  }

  function wireSelectors(): void {
    document.getElementById('urep-pick-from')?.addEventListener('click', pickFrom);
    document.getElementById('urep-pick-to')?.addEventListener('click', pickTo);
    document.getElementById('urep-preview-btn')?.addEventListener('click', () => void runPreview());
  }

  function wirePreviewTable(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-urep-pick]').forEach((b) => {
      b.addEventListener('click', () => pickRowReplacement(parseInt(b.dataset['urepPick'] || '0')));
    });
    document.querySelectorAll<HTMLButtonElement>('[data-urep-skip]').forEach((b) => {
      b.addEventListener('click', () => toggleSkip(parseInt(b.dataset['urepSkip'] || '0')));
    });
    document.querySelectorAll<HTMLButtonElement>('[data-urep-restore]').forEach((b) => {
      b.addEventListener('click', () => toggleSkip(parseInt(b.dataset['urepRestore'] || '0')));
    });
    document.getElementById('urep-apply-btn-top')?.addEventListener('click', confirmApply);
    document.getElementById('urep-apply-btn-bottom')?.addEventListener('click', confirmApply);
  }

  function wireRecap(): void {
    document.getElementById('urep-export-csv')?.addEventListener('click', exportRecapCsv);
    document.getElementById('urep-restart')?.addEventListener('click', () => {
      reset();
      render();
    });
  }

  pages.userReplacement = async function () {
    reset();
    render();
    // Garde anti-perte (audit UX 2026-06) : un plan de remplacement
    // prévisualisé (décisions ligne à ligne) mais pas encore appliqué est
    // perdu si l'utilisateur quitte la page. On avertit avant.
    setUnsavedGuard(() => state.rows.length > 0 && !state.applyResult);
  };
})();

export {};
